import { useState } from "react";
import { Moon, MessageCircleQuestion, ChevronDown } from "lucide-react";

const TOP_N = 5;

// Under the volume chart: the TOP 5 things people asked about (and how much the AI closed alone)
// + after-hours share. Everything past five folds behind "Show all".
export function AskedAndAfterHours({ topics = [], afterHours }) {
  const [showAll, setShowAll] = useState(false);
  const sorted = [...topics].sort((a, b) => (b.conversations || 0) - (a.conversations || 0));
  const shown = showAll ? sorted : sorted.slice(0, TOP_N);
  const hidden = sorted.length - shown.length;
  const restConv = sorted.slice(TOP_N).reduce((n, t) => n + (t.conversations || 0), 0);
  const max = Math.max(1, ...sorted.map((t) => t.conversations));
  const ah = afterHours || {};
  const byHour = ah.by_hour || [];
  const hourMax = Math.max(1, ...byHour);
  const fmtHour = (h) => (h === 0 ? "12a" : h < 12 ? `${h}a` : h === 12 ? "12p" : `${h - 12}p`);
  const bh = ah.business_hours || {};
  const bhStart = parseInt((bh.start || "09:00").split(":")[0], 10);
  const bhEnd = parseInt((bh.end || "17:00").split(":")[0], 10);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 border-t border-[#F0EDE6] pt-5" data-testid="asked-after-hours">
      <div className="lg:col-span-3">
        <div className="flex items-center gap-2 mb-3">
          <MessageCircleQuestion className="h-4 w-4 text-redline-500" strokeWidth={2} />
          <h3 className="font-heading font-bold text-sm text-[#1C1917]">What they asked about</h3>
          <span className="text-[11px] text-[#A8A29E]">· top {Math.min(TOP_N, sorted.length) || TOP_N} topics, and how many the AI closed alone</span>
        </div>
        {topics.length === 0 ? (
          <p className="text-sm text-[#A8A29E]">No conversations in this range yet.</p>
        ) : (
          <ul className="space-y-2" data-testid="topic-list">
            {shown.map((t) => {
              const pct = Math.round((t.conversations / max) * 100);
              const aiPct = t.conversations ? Math.round((t.ai_alone / t.conversations) * 100) : 0;
              return (
                <li key={t.topic}>
                  <div data-testid={`topic-row-${t.topic}`} className="w-full text-left group">
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="font-semibold text-[#1C1917]">{t.label}</span>
                      <span className="tabular-nums text-[#57534E]">{t.conversations} · <span className="text-violet-600 font-semibold">{aiPct}% AI alone</span></span>
                    </div>
                    <div className="h-2 rounded-full bg-sand-100 overflow-hidden">
                      <div className="h-full rounded-full bg-sand-300" style={{ width: `${pct}%` }}>
                        <div className="h-full bg-violet-400 rounded-full" style={{ width: `${aiPct}%` }} />
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {sorted.length > TOP_N && (
          <button type="button" data-testid="topics-toggle-all" onClick={() => setShowAll((v) => !v)} aria-expanded={showAll}
            className="mt-2.5 inline-flex items-center gap-1 text-xs font-bold text-[#57534E] hover:text-[#1C1917] transition-colors">
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showAll ? "rotate-180" : ""}`} />
            {showAll ? "Show top 5 only" : `Show all ${sorted.length} topics · ${hidden} more (${restConv} conversation${restConv === 1 ? "" : "s"})`}
          </button>
        )}
      </div>
      <div className="lg:col-span-2">
        <div className="flex items-center gap-2 mb-3">
          <Moon className="h-4 w-4 text-indigo-500" strokeWidth={2} />
          <h3 className="font-heading font-bold text-sm text-[#1C1917]">After hours</h3>
        </div>
        <div className="flex items-end gap-3">
          <div data-testid="after-hours-pct" className="font-heading text-3xl font-black tracking-tight text-[#1C1917] leading-none tabular-nums">
            {ah.pct != null ? `${ah.pct}%` : "—"}
          </div>
          <div className="text-xs text-[#57534E] pb-0.5">
            of texts &amp; calls came in outside {bh.start || "09:00"}–{bh.end || "17:00"} or on a weekend
            {ah.total ? <span className="text-[#A8A29E]"> · {ah.count} of {ah.total}</span> : null}
          </div>
        </div>
        <div className="mt-3">
          <div className="flex items-end gap-[2px] h-12" data-testid="by-hour-strip" title="Texts & calls by hour of day (community time)">
            {byHour.map((n, h) => {
              const inHours = h >= bhStart && h < bhEnd;
              return (
                <div key={h} className="flex-1 flex flex-col justify-end" title={`${fmtHour(h)} · ${n}`}>
                  <div className={`w-full rounded-t ${inHours ? "bg-sand-300" : "bg-indigo-400"}`}
                    style={{ height: `${Math.max(n ? 8 : 2, Math.round((n / hourMax) * 100))}%` }} />
                </div>
              );
            })}
          </div>
          <div className="flex justify-between text-[10px] text-[#A8A29E] mt-1">
            <span>12a</span><span>6a</span><span>noon</span><span>6p</span><span>11p</span>
          </div>
          <div className="flex items-center gap-3 text-[10px] text-[#8A8378] mt-1.5">
            <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-sand-300" /> office hours</span>
            <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-indigo-400" /> after hours — the concierge covered these</span>
          </div>
        </div>
      </div>
    </div>
  );
}
